import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Radio, MapPin, CheckCircle2, MessageSquare, AlertTriangle, EyeOff, Loader2 } from "lucide-react";

function RcMedia({ m }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    client.get(`/media-sessions/media/${m.path}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [m.path]);
  if (!src) return <Loader2 size={12} className="animate-spin text-[#9CA3AF]" />;
  return m.kind === "video"
    ? <video src={src} controls className="w-40 rounded-sm border border-[#2C2D35]" />
    : <img src={src} alt="" className="w-20 h-16 object-cover rounded-sm border border-[#2C2D35]" />;
}

// Responder Connect — dispatcher-shared caller media on the MDT (Add-on 2).
export default function ResponderConnect({ call }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const load = () => client.get(`/incidents/${call.id}/responder-connect`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [call.id]); // eslint-disable-line

  if (!data?.enabled || !(data.sessions || []).length) return null;
  const act = async (action, note = "") => {
    if (busy) return;
    setBusy(true);
    try { await client.post(`/incidents/${call.id}/responder-connect`, { action, note }); toast.success("Logged to incident timeline"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setTimeout(() => setBusy(false), 1500);
  };
  return (
    <div className="rounded-lg border border-[#A855F7]/50 bg-[#141518] p-3 mt-3" data-testid="responder-connect">
      <div className="font-head text-[11px] font-bold tracking-wider text-[#A855F7] mb-2 flex items-center gap-1.5">
        <Radio size={12} /> RESPONDER CONNECT — DISPATCH-SHARED CALLER MEDIA
      </div>
      {data.sessions.map((s) => (
        <div key={s.id} className="mb-2">
          {!hidden && s.video_active && s.live_frame && (
            <div className="mb-2">
              <div className="font-mono text-[9px] text-[#EF4444] font-bold animate-pulse mb-0.5">● LIVE CALLER VIDEO (caller-authorized)</div>
              <img src={s.live_frame.image} alt="Live" className="w-full max-w-[260px] rounded-sm border border-[#EF4444]" data-testid="rc-live-frame" />
            </div>
          )}
          {(s.locations || []).length > 0 && (
            <div className="font-mono text-[10px] text-[#C9CDD4] mb-1" data-testid="rc-location">
              <MapPin size={10} className="inline text-[#22D3EE] mr-1" />
              Caller: {s.locations[s.locations.length - 1].lat.toFixed(5)}, {s.locations[s.locations.length - 1].lng.toFixed(5)}
              ±{Math.round(s.locations[s.locations.length - 1].accuracy)}m · {s.locations.length} breadcrumb(s)
            </div>
          )}
          {(s.media || []).length > 0 && <div className="flex flex-wrap gap-1.5 mb-1.5">{s.media.map((m, i) => <RcMedia key={i} m={m} />)}</div>}
        </div>
      ))}
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        <button data-testid="rc-ack" disabled={busy} onClick={() => act("acknowledge")} className="px-3 py-1.5 text-[10px] font-head font-bold bg-[#10B981] text-black rounded-sm disabled:opacity-50"><CheckCircle2 size={11} className="inline mr-0.5" />ACKNOWLEDGE</button>
        <button data-testid="rc-supervisor" disabled={busy} onClick={() => act("request_supervisor")} className="px-3 py-1.5 text-[10px] font-head font-bold border border-[#F59E0B] text-[#F59E0B] rounded-sm disabled:opacity-50">REQUEST SUPERVISOR</button>
        <button data-testid="rc-media-unavail" disabled={busy} onClick={() => act("media_unavailable")} className="px-3 py-1.5 text-[10px] font-head font-bold border border-[#9CA3AF] text-[#9CA3AF] rounded-sm disabled:opacity-50"><AlertTriangle size={11} className="inline mr-0.5" />MEDIA UNAVAILABLE</button>
        <button data-testid="rc-wrong-loc" disabled={busy} onClick={() => act("wrong_location")} className="px-3 py-1.5 text-[10px] font-head font-bold border border-[#EF4444] text-[#EF4444] rounded-sm disabled:opacity-50"><MapPin size={11} className="inline mr-0.5" />WRONG LOCATION</button>
        <button data-testid="rc-stop-view" disabled={busy} onClick={() => { setHidden(!hidden); if (!hidden) act("stop_viewing"); }} className="px-3 py-1.5 text-[10px] font-head font-bold border border-[#6B7280] text-[#6B7280] rounded-sm disabled:opacity-50"><EyeOff size={11} className="inline mr-0.5" />{hidden ? "RESUME VIEWING" : "STOP VIEWING MEDIA"}</button>
      </div>
      <div className="flex gap-1">
        <input data-testid="rc-msg-input" value={msg} onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && msg.trim()) { act("message", msg.trim()); setMsg(""); } }}
          placeholder="Message dispatch…" className="flex-1 bg-[#0d0e11] border border-[#2C2D35] px-2 py-1 text-[10px] font-mono text-white rounded-sm outline-none" />
        <button data-testid="rc-msg-send" onClick={() => { if (msg.trim()) { act("message", msg.trim()); setMsg(""); } }}
          className="px-2 py-1 text-[9px] font-head font-bold bg-[#A855F7] text-white rounded-sm"><MessageSquare size={10} /></button>
      </div>
    </div>
  );
}
